// ============================================================================
// Building lifecycle — READ, never computed (2026-09-25).
//
// The database decides whether a building is new, starting soon, behind,
// recently started or started: public.building_lifecycle(), surfaced as the
// `lifecycle` column on v_am_account_cards / v_account_management_cards and
// v_new_account_lifecycle. Confirmed Active always beats a planned date.
// This module only translates that answer into what a screen shows. It must
// never compare a date to today — the guard test in lifecycle-one-rule.test.ts
// fails if a window or date arithmetic comes back.
// ============================================================================

import type { FacilityLifecycle } from './design-tokens';

export type BuildingLifecycle =
  | 'draft'
  | 'cancelled'
  | 'recent_start'
  | 'started'
  | 'behind'
  | 'starting_soon'
  | 'new';

/** The My Accounts lens pill for a building. Lifecycle first; a building with
 *  no lens (started, closed, or not in the card view) falls back to its status
 *  pill when not Active, exactly as before. */
export function lensFor(
  lifecycle: string | null | undefined,
  status: string | null | undefined,
): FacilityLifecycle | undefined {
  if (lifecycle === 'starting_soon') return 'Starting soon';
  if (lifecycle === 'recent_start') return 'Recent';
  if (lifecycle === 'behind') return 'Behind';
  if (status && status !== 'Active') return status as FacilityLifecycle;
  return undefined;
}

/** "Starts <date>" is shown only for a building that has not opened. A
 *  confirmed building never says it is about to start. */
export function showsStartLabel(lifecycle: string | null | undefined): boolean {
  return lifecycle === 'new' || lifecycle === 'starting_soon' || lifecycle === 'behind';
}

/** Parse YYYY-MM-DD to local midnight (no timezone shift). */
function parseLocalDate(value: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** The "Starts M/D" label — display only, gated on lifecycle. null when the
 *  building has opened or has no planned date. */
export function startLabel(
  lifecycle: string | null | undefined,
  plannedStartDate: string | null | undefined,
): string | null {
  if (!showsStartLabel(lifecycle) || !plannedStartDate) return null;
  const d = parseLocalDate(plannedStartDate);
  return d ? `Starts ${d.getMonth() + 1}/${d.getDate()}` : null;
}
